import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Tv, ShoppingBag, BookOpen, GraduationCap, User, IdCard, CalendarClock, Sparkles, MapPin, KeyRound, Save } from "lucide-react";
import { PonderrTVPanel } from "../student_tv/StudentPonderrTV";
import { KitStorePanel } from "../student_store/StudentKitStore";
import StudentPonderrLearn from "../ponderr_learn/StudentPonderrLearn";
import { Ponderr } from "../StudentDashboard";
import { saveLocalKey, loadLocalKey } from "@/lib/idb";
import WelcomeTour from "./WelcomeTour";
import AppCacheControls from "@/components/shared/AppCacheControls";

const TABS = [
  { id: "ponderr_tv", label: "Ponderr TV", icon: Tv },
  { id: "storez", label: "Storez", icon: ShoppingBag },
  { id: "ponderr_learn", label: "Ponderr Learn", icon: BookOpen },
  { id: "lms", label: "Ponderr LMS", icon: GraduationCap },
  { id: "my_space", label: "My Space", icon: User },
];

function daysLeft(iso) {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

function ByokSettings() {
  const [draft, setDraft] = useState("");
  const [hint, setHint] = useState("");
  useEffect(() => {
    api.get("/open-school/settings").then(r => {
      setHint(r.data.gemini_key_hint || "");
      const k = r.data.gemini_key || loadLocalKey();
      if (k) { saveLocalKey(k); setDraft(k); }
    }).catch(() => { const k = loadLocalKey(); if (k) setDraft(k); });
  }, []);
  const save = async () => {
    const k = draft.trim(); saveLocalKey(k);
    try { const r = await api.put("/open-school/settings", { gemini_key: k });
      setHint(k ? "••••" + k.slice(-4) : ""); toast.success("Gemini key saved");
    } catch { toast.error("Saved locally; cloud sync failed"); }
  };
  return (
    <div className="nb-card p-5 border-2 border-black bg-[#FDE047]" data-testid="myspace-byok">
      <div className="flex items-center gap-2 text-xs font-bold uppercase"><KeyRound size={16} /> Your Personal Gemini API Key</div>
      <p className="text-xs mt-1 mb-2">Free from Google AI Studio. Powers the Ponderr Agent tutor in your Notebook using your own quota. {hint && <b>Current: {hint}</b>}</p>
      <div className="flex gap-2">
        <input type="password" className="nb-input flex-1 text-sm" placeholder="AIza…" value={draft} onChange={e => setDraft(e.target.value)} data-testid="myspace-byok-input" />
        <button className="nb-btn" onClick={save} data-testid="myspace-byok-save"><Save size={15} /> Save</button>
      </div>
      <a className="text-xs underline mt-2 inline-block" href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">Get a free key →</a>
    </div>
  );
}

function MySpace({ me }) {
  if (!me) return <div className="mono text-sm">Loading your space…</div>;
  const left = daysLeft(me.trial_expires_at);
  const free = me.free_override;
  return (
    <div className="space-y-4" data-testid="open-my-space">
      <div className="nb-card p-6 bg-[#DDD6FE] border-2 border-black">
        <div className="flex items-center gap-2 text-[11px] uppercase font-bold tracking-wide text-slate-700"><IdCard size={16} /> Ponderr Open School ID</div>
        <div className="display text-2xl md:text-3xl font-black mono tracking-tight mt-1" data-testid="open-my-id">{me.open_student_id}</div>
        <div className="text-sm mt-1">{me.name} · {me.email}</div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className={`nb-card p-5 border-2 border-black ${free ? "bg-[#86EFAC]" : left <= 7 ? "bg-[#FCA5A5]" : "bg-[#FDE047]"}`} data-testid="open-my-trial">
          <div className="flex items-center gap-2 text-xs font-bold uppercase">{free ? <Sparkles size={16} /> : <CalendarClock size={16} />}{free ? "Free Access" : "Free Trial"}</div>
          <div className="display text-2xl font-black mt-1">{free ? "Unlocked" : `${left} days left`}</div>
          {!free && <div className="text-xs mt-1">Until {(me.trial_expires_at || "").slice(0, 10)}</div>}
        </div>
        <div className="nb-card p-5 border-2 border-black bg-white" data-testid="open-my-academics">
          <div className="flex items-center gap-2 text-xs font-bold uppercase"><GraduationCap size={16} /> Academics</div>
          <div className="display text-xl font-black mt-1">{me.grade}</div>
          <div className="text-sm">Board: <b>{me.board_code}</b></div>
        </div>
        <div className="nb-card p-5 border-2 border-black bg-white" data-testid="open-my-location">
          <div className="flex items-center gap-2 text-xs font-bold uppercase"><MapPin size={16} /> Location</div>
          <div className="display text-base font-black mt-1">{me.city}</div>
          <div className="text-sm">{me.state}, {me.country_name}</div>
          <div className="text-xs text-slate-500">{me.district} · {me.postal_code}</div>
        </div>
      </div>
      <ByokSettings />
      <div className="nb-card p-5 border-2 border-black bg-white" data-testid="myspace-app-controls">
        <div className="text-xs font-bold uppercase mb-2">App & Cache</div>
        <AppCacheControls inline />
      </div>
      <div className="nb-card p-5 border-2 border-black bg-white text-sm text-slate-600">
        Tip: swipe from the <b>left edge</b> for your Daily Organiser and the <b>right edge</b> for your Ponderr Notebook &amp; AI Tutor.
      </div>
    </div>
  );
}

export default function OpenStudentConsole() {
  const { user } = useAuth();
  const [tab, setTab] = useState("my_space");
  const [me, setMe] = useState(null);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    api.get("/open-school/me").then(r => {
      setMe(r.data);
      if (!r.data.has_completed_welcome_tour) setShowTour(true);
    }).catch(() => {});
  }, []);

  return (
    <Shell title="Ponderr Open School" subtitle={user?.name} tabs={TABS} active={tab} onTab={setTab}>
      {tab === "ponderr_tv" && <PonderrTVPanel />}
      {tab === "storez" && <KitStorePanel />}
      {tab === "ponderr_learn" && <StudentPonderrLearn />}
      {tab === "lms" && <Ponderr />}
      {tab === "my_space" && <MySpace me={me} />}
      {showTour && <WelcomeTour onNavigateTab={setTab} onDone={() => setShowTour(false)} />}
    </Shell>
  );
}
